/** Sincroniza as preferências locais com o servidor (US-03 / TASK-07).
 *
 * Duas direções, com regras diferentes:
 *
 * **Servidor → local**, uma vez ao entrar: o aluno que trocou o tema no celular
 * encontra o tema trocado no laptop. Resolve por last-write-wins pelo carimbo do
 * cliente (N19), e NUNCA sobrescreve alteração local ainda não enviada — quem
 * acabou de mexer no controle está olhando para ele.
 *
 * **Local → servidor**, com espera: arrastar o slider de tamanho emite um evento
 * por pixel. Sem o *debounce* isso seriam ~40 PUTs num gesto, o que o rate limit
 * de preferências recusaria — e o aluno veria o próprio controle falhar.
 *
 * Falha de rede não desfaz nada: a preferência já está pintada e no espelho
 * local. O que fica pendente é reenviado no próximo `flush`, inclusive o do
 * `pagehide` — fechar a aba logo depois de trocar o tema é o caso comum, e sem
 * isso a escolha morre com a aba.
 */
import { useCallback, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'

import { useAccessibilityStore } from '@/features/reader/stores/accessibilityStore'
import { problemStatus } from '@/services/api'
import { fetchPreferences, savePreferences } from '@/services/preferencesService'
import type { ReaderPreferences, ReaderPreferencesResponse } from '@/types/api'

/** Tempo parado antes de enviar. Longo o bastante para um gesto de slider. */
export const ESPERA_MS = 800

interface Dependencias {
  buscar?: () => Promise<ReaderPreferencesResponse>
  salvar?: (p: ReaderPreferences, carimbo: string) => Promise<ReaderPreferencesResponse>
  habilitado?: boolean
}

export function useReaderPreferences(deps: Dependencias = {}) {
  const { buscar = fetchPreferences, salvar = savePreferences, habilitado = true } = deps

  const hidratarDoServidor = useAccessibilityStore((s) => s.hidratarDoServidor)
  const marcarSalvo = useAccessibilityStore((s) => s.marcarSalvo)
  const preferencias = useAccessibilityStore((s) => s.preferencias)
  const carimbo = useAccessibilityStore((s) => s.carimbo)
  const pendente = useAccessibilityStore((s) => s.pendente)

  const query = useQuery({
    queryKey: ['reader-preferences'],
    queryFn: buscar,
    enabled: habilitado,
    retry: false,
    // Uma vez por sessão: refetch depois de o aluno mexer nos controles traria
    // de volta o estado antigo do servidor no meio do ajuste.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

  const remoto = query.data
  useEffect(() => {
    if (remoto) hidratarDoServidor(remoto)
  }, [remoto, hidratarDoServidor])

  // Uma ref com o valor do momento, atualizada por efeito (escrever ref durante
  // o render é o que o `react-hooks/refs` proíbe, e com razão). É o que permite
  // ao `enviar` ter identidade ESTÁVEL: sem isso, o efeito do `pagehide` se
  // reassinaria a cada tecla do slider, e o do desmonte dispararia um envio a
  // cada alteração em vez de um no fim.
  const ultimo = useRef({ preferencias, carimbo, pendente, habilitado, salvar, marcarSalvo })
  useEffect(() => {
    ultimo.current = { preferencias, carimbo, pendente, habilitado, salvar, marcarSalvo }
  })

  const enviar = useCallback(() => {
    const atual = ultimo.current
    if (!atual.pendente || !atual.carimbo || !atual.habilitado) return
    const carimboEnviado = atual.carimbo
    void atual
      .salvar(atual.preferencias, carimboEnviado)
      .then(() => {
        // Só limpa o pendente se nada mudou desde o envio; senão o próximo
        // ciclo perderia a alteração feita durante a request.
        if (ultimo.current.carimbo === carimboEnviado) atual.marcarSalvo()
      })
      .catch(() => {
        /* fica pendente: a preferência já vale localmente e reenvia no próximo */
      })
  }, [])

  useEffect(() => {
    if (!pendente) return
    const id = setTimeout(enviar, ESPERA_MS)
    return () => clearTimeout(id)
  }, [pendente, carimbo, enviar])

  useEffect(() => {
    // `pagehide` e não `beforeunload`: o Safari no iOS não dispara o segundo ao
    // trocar de aba, que é como o celular "fecha" o leitor.
    window.addEventListener('pagehide', enviar)
    return () => {
      window.removeEventListener('pagehide', enviar)
      enviar()
    }
  }, [enviar])

  return {
    preferencias,
    /** 404 = backend sem o endpoint; o painel segue valendo só localmente. */
    sincronizando: !(query.isError && problemStatus(query.error) === 404),
    carregando: query.isLoading,
  }
}
